import type { Product } from "@/types/content";

/**
 * The five connected product surfaces that make up Devory, in the order
 * shown on /product. Status and capability wording are grounded in the
 * platform's own engineering programme records (`DEVORY_MASTER_PROGRAMME.md`)
 * — update this file only when that underlying evidence changes, and record
 * the source in the accompanying commit, per docs/CONTENT_GUIDE.md.
 */
export const products: Product[] = [
  {
    id: "platform",
    name: "Devory Platform",
    tagline:
      "The graph-native engineering platform: software modelled as components, interfaces, capabilities, providers, graphs and revisions.",
    status: "in-development",
    capabilities: [
      "Visual graph composition",
      "Component and capability modelling",
      "Typed interfaces and provider binding",
      "Project graphs and revisions",
      "Qualification before reuse",
      "Distribution through the Marketplace",
    ],
    link: { label: "Explore the canvas", to: "/canvas" },
    keycap: "connect",
  },
  {
    id: "desktop",
    name: "Devory Desktop",
    tagline:
      "The native engineering environment for local projects, graph editing, persistence and controlled runtime execution.",
    status: "in-development",
    capabilities: [
      "Local project management",
      "Native graph workspace",
      "Local persistence",
      "Runtime integration and controlled execution",
      "Recovery and revision workflows",
      "Optional Cloud connectivity",
    ],
    link: { label: "How Desktop connects to Cloud", to: "/cloud" },
    keycap: "workspace",
  },
  {
    id: "cloud",
    name: "Devory Cloud",
    tagline:
      "The browser-based collaborative application for identity, organisations, shared projects and Cloud-connected workflows.",
    status: "live",
    capabilities: [
      "Self-serve sign-up and sign-in",
      "Organisation and workspace access",
      "Real billing and plan management",
      "Shared projects and graphs",
      "Scoped cloud workflow execution",
      "Marketplace browsing",
    ],
    link: { label: "Read about Devory Cloud", to: "/cloud" },
    keycap: "cloud",
  },
  {
    id: "runtime",
    name: "Devory Runtime",
    tagline:
      "The execution and qualification foundation that turns authorised graphs into deterministic, controlled, auditable operations.",
    status: "pilot",
    capabilities: [
      "Planning and scheduling",
      "Real subprocess execution",
      "Journalling and recovery",
      "Policy enforcement",
      "Component qualification",
      "Controlled provider interaction",
    ],
    link: { label: "See how qualification works", to: "/qualification" },
    keycap: "runtime",
  },
  {
    id: "aui",
    name: "Devory Autonomous User Interface",
    tagline:
      "The governed interface through which external AI agents act on behalf of authenticated users, scoped, confirmed and audited, never unrestricted.",
    status: "in-development",
    capabilities: [
      "Authenticated user delegation",
      "Capability discovery",
      "Scoped resource requests",
      "Confirmation and approval boundaries",
      "Audited operation outcomes",
      "Revocation and fail-closed enforcement",
    ],
    link: { label: "Read about Devory AUI", to: "/agent-user-interface" },
    keycap: "ai",
  },
];
